import { cursorPosition } from './cursor-trail.ts'
import type { CursorPosition } from './cursor-trail.ts'
import type { VaultApprovalState } from './vault-api-approvals.ts'

/**
 * The /vault/approvals URL state: which tab, how many rows, how far in.
 *
 * In the URL rather than in component state for the reason
 * `sessions-search.ts` spells out and `VaultAudit.tsx` demonstrates: this
 * route's loader re-runs every 10 seconds, and every run replaces the
 * loader data the page renders from. Paging kept in a `useState` survives
 * the tick as a number but immediately disagrees with the rows beside it.
 *
 * Pure and React-free: `validateSearch` needs these at
 * router-construction time, and the rules are worth testing without a DOM.
 */

export const APPROVAL_TABS: readonly VaultApprovalState[] = [
  'pending',
  'approved',
  'denied',
  'expired',
]

/**
 * Page sizes offered in the picker.
 *
 * Not `DEFAULT_PAGE_SIZES` from `ui/Pagination.tsx` (25/50/100/250): the
 * API caps `limit` at 200, so a 250 option would be silently clamped and
 * the picker would then lie about what it fetched.
 */
export const APPROVAL_PAGE_SIZES: readonly number[] = [10, 25, 50, 100]

/**
 * The page size when the URL does not name one.
 *
 * Deliberately not the server's own default of 50. The queue is read as a
 * worklist — the question is "what is waiting on me right now" — and a
 * screenful is the size that makes it answerable. The route always sends
 * an explicit `limit`, so this is the only place the number is decided.
 */
export const DEFAULT_APPROVAL_PAGE_SIZE = 25

/** The raw search params, exactly as they appear in the URL. Every field
 * is optional, so the common view (pending, 25 rows, page 1) has a clean
 * `/vault/approvals` with nothing on it. */
export interface ApprovalsSearch {
  tab?: VaultApprovalState
  pageSize?: number
  /** The cursors used to reach this page, oldest first, comma-joined.
   * Absent on page 1. See `cursor-trail.ts`. */
  cursors?: string
}

export interface ResolvedApprovalsView extends CursorPosition {
  tab: VaultApprovalState
  pageSize: number
}

/**
 * The three field rules, each total over `unknown`.
 *
 * Written this way so `parseApprovalsSearch` and `resolveApprovalsView`
 * cannot drift: the loader resolves the view from its own `loaderDeps`,
 * and depending on `validateSearch` having already run first would make
 * the request the loader sends a function of the router's internals. It
 * is cheap to be total here and it means a `?pageSize=9000` reaches the
 * API as the default rather than as a 400 inside a loader, which is an
 * error boundary and not a view.
 */
function pickTab(value: unknown): VaultApprovalState | undefined {
  return typeof value === 'string' &&
    (APPROVAL_TABS as readonly string[]).includes(value)
    ? (value as VaultApprovalState)
    : undefined
}

function pickPageSize(value: unknown): number | undefined {
  const size = Number(value)
  return APPROVAL_PAGE_SIZES.includes(size) ? size : undefined
}

function pickCursors(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined
}

/**
 * `validateSearch` for the route.
 *
 * Hostile input is dropped rather than rejected: a mangled `?tab=cat` in a
 * pasted link should land the operator on the queue, not on an error
 * boundary. The server validates — it answers 400 for a bad cursor or an
 * out-of-range limit — and this only has to stop the admin from asking an
 * obviously broken question.
 */
export function parseApprovalsSearch(
  search: Record<string, unknown>,
): ApprovalsSearch {
  const parsed: ApprovalsSearch = {}
  const tab = pickTab(search.tab)
  if (tab !== undefined) parsed.tab = tab
  const pageSize = pickPageSize(search.pageSize)
  if (pageSize !== undefined) parsed.pageSize = pageSize
  const cursors = pickCursors(search.cursors)
  if (cursors !== undefined) parsed.cursors = cursors
  return parsed
}

export function resolveApprovalsView(
  search: ApprovalsSearch,
): ResolvedApprovalsView {
  return {
    // `pending` is what an operator opens this page for; the other three
    // are history.
    tab: pickTab(search.tab) ?? 'pending',
    pageSize: pickPageSize(search.pageSize) ?? DEFAULT_APPROVAL_PAGE_SIZE,
    // `cursorTrail` drops anything that is not a uuid, so a hand-edited
    // trail degrades to page 1 rather than to a 400.
    ...cursorPosition(pickCursors(search.cursors)),
  }
}
